import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { api, BACKEND, formatError, formatPrice, imgUrl } from "../../lib/api";
import { AdminTitle, AdminField, AdminCheck, Panel } from "./AdminUi";

const EMPTY = { name: "", categories: [], brand: "", weight_g: 250, weight_verified: false, price: "", volume_ml: 0, stock: "", family: "", description: "", images: [],
  notes_top: "", notes_heart: "", notes_base: "", is_new: false, is_featured: false, is_example: false, active: true,
  regulatory: { ingredients: "", precautions: "", manufacturer: "", other: "" }, seo_title: "", seo_description: "" };

const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const ImagesEditor = ({ images, onChange }) => {
  const fileRef = useRef();
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); onChange([...images, data.url]); toast.success("Photo importée"); }
    catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="sm:col-span-2">
      <span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Photos</span>
      <div className="mt-2 flex flex-wrap gap-3">
        {images.map((img, i) => (
          <div key={img + i} className="relative h-24 w-20">
            <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center" aria-label="Retirer"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current.click()} disabled={uploading} className="h-24 w-20 border border-dashed border-[#C9A45C]/40 flex flex-col items-center justify-center text-[10px] text-[#C9A45C] gap-1" data-testid="product-image-upload-button">
          <Upload className="h-4 w-4" />{uploading ? "Envoi…" : "Importer"}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="product-image-file-input" />
      </div>
      <div className="flex mt-3">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…ou coller l'URL d'une image (https://)" className="flex-1 bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2 text-sm outline-none" data-testid="product-image-url-input" />
        <button type="button" onClick={() => { if (url.startsWith("https://")) { onChange([...images, url]); setUrl(""); } }} className="px-4 border border-l-0 border-[#C9A45C]/25 text-xs" data-testid="product-image-url-add">Ajouter</button>
      </div>
    </div>
  );
};

const ProductForm = ({ initial, cats, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setR = (k) => (e) => setF({ ...f, regulatory: { ...f.regulatory, [k]: e.target.value } });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.categories.length) { toast.error("Choisissez au moins une catégorie"); return; }
    setSaving(true);
    const body = { ...f, price: parseFloat(f.price), volume_ml: parseInt(f.volume_ml, 10) || 0, stock: parseInt(f.stock, 10), weight_g: parseInt(f.weight_g, 10) || 0,
      notes_top: toList(f.notes_top), notes_heart: toList(f.notes_heart), notes_base: toList(f.notes_base) };
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, body); else await api.post("/admin/products", body);
      toast.success("Produit enregistré"); onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="product-form">
      <AdminField className="sm:col-span-2" label="Nom du parfum" required value={f.name} onChange={set("name")} testid="product-form-name" />
      <div className="sm:col-span-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Catégories (une ou plusieurs)</span>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="product-form-categories">
          {cats.map((c) => {
            const on = f.categories.includes(c.slug);
            return (
              <button type="button" key={c.slug} onClick={() => setF({ ...f, categories: on ? f.categories.filter((x) => x !== c.slug) : [...f.categories, c.slug] })}
                className={`px-3 py-1.5 text-xs border ${on ? "bg-[#C9A45C] text-[#0B0B0B] border-[#C9A45C]" : "border-[#C9A45C]/30 text-[#F8F6F1]/70"}`} data-testid={`product-form-cat-${c.slug}`}>{c.name}</button>
            );
          })}
        </div>
      </div>
      <AdminField label="Marque (si vérifiée)" value={f.brand} onChange={set("brand")} testid="product-form-brand" />
      <AdminField label="Famille olfactive (si vérifiée)" value={f.family} onChange={set("family")} placeholder="Oriental, Boisé, Floral…" testid="product-form-family" />
      <AdminField label="Prix (€ TTC)" type="number" step="0.01" min="0" required value={f.price} onChange={set("price")} testid="product-form-price" />
      <AdminField label="Contenance (ml, 0 = à confirmer)" type="number" min="0" required value={f.volume_ml} onChange={set("volume_ml")} testid="product-form-volume" />
      <AdminField label="Stock" type="number" min="0" required value={f.stock} onChange={set("stock")} testid="product-form-stock" />
      <AdminField label="Poids (g)" type="number" min="0" required value={f.weight_g} onChange={set("weight_g")} testid="product-form-weight" />
      <div className="flex flex-wrap items-end gap-4 pb-2 sm:col-span-2">
        <AdminCheck label="Poids vérifié" checked={f.weight_verified} onChange={(v) => setF({ ...f, weight_verified: v })} testid="product-form-weight-verified" />
        <AdminCheck label="Nouveauté" checked={f.is_new} onChange={(v) => setF({ ...f, is_new: v })} testid="product-form-is-new" />
        <AdminCheck label="Coup de cœur" checked={f.is_featured} onChange={(v) => setF({ ...f, is_featured: v })} testid="product-form-is-featured" />
        <AdminCheck label="Exemple" checked={f.is_example} onChange={(v) => setF({ ...f, is_example: v })} testid="product-form-is-example" />
        <AdminCheck label="Visible" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="product-form-active" />
      </div>
      <AdminField className="sm:col-span-2" textarea rows={4} label="Description" value={f.description} onChange={set("description")} testid="product-form-description" />
      <ImagesEditor images={f.images} onChange={(images) => setF({ ...f, images })} />
      <p className="sm:col-span-2 eyebrow mt-2">Notes olfactives (séparées par des virgules, uniquement si confirmées)</p>
      <AdminField label="Notes de tête" value={f.notes_top} onChange={set("notes_top")} testid="product-form-notes-top" />
      <AdminField label="Notes de cœur" value={f.notes_heart} onChange={set("notes_heart")} testid="product-form-notes-heart" />
      <AdminField className="sm:col-span-2" label="Notes de fond" value={f.notes_base} onChange={set("notes_base")} testid="product-form-notes-base" />
      <p className="sm:col-span-2 eyebrow mt-2">Informations réglementaires</p>
      <AdminField className="sm:col-span-2" textarea rows={2} label="Composition (INCI)" value={f.regulatory.ingredients} onChange={setR("ingredients")} testid="product-form-ingredients" />
      <AdminField textarea rows={2} label="Précautions d'emploi" value={f.regulatory.precautions} onChange={setR("precautions")} />
      <AdminField textarea rows={2} label="Fabricant / responsable" value={f.regulatory.manufacturer} onChange={setR("manufacturer")} />
      <AdminField className="sm:col-span-2" textarea rows={2} label="Autres mentions" value={f.regulatory.other} onChange={setR("other")} />
      <p className="sm:col-span-2 eyebrow mt-2">Référencement (facultatif)</p>
      <AdminField label="Titre SEO" value={f.seo_title} onChange={set("seo_title")} />
      <AdminField label="Description SEO" value={f.seo_description} onChange={set("seo_description")} />
      <div className="sm:col-span-2 flex gap-3 justify-end mt-4">
        {f.slug && <a href={`${BACKEND}/api/share/image/${f.slug}.jpg`} target="_blank" rel="noopener noreferrer" className="mr-auto self-center text-xs text-[#C9A45C] underline" data-testid="product-share-preview-link">Voir l'aperçu de partage</a>}
        <button type="button" onClick={onClose} className="btn-outline-gold !py-3">Annuler</button>
        <button type="submit" disabled={saving} className="btn-gold !py-3" data-testid="product-form-submit">{saving ? "Enregistrement…" : "Enregistrer"}</button>
      </div>
    </form>
  );
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const edit = (p) => setEditing({ ...EMPTY, ...p, notes_top: (p.notes_top || []).join(", "), notes_heart: (p.notes_heart || []).join(", "), notes_base: (p.notes_base || []).join(", "), regulatory: { ...EMPTY.regulatory, ...(p.regulatory || {}) } });
  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Produit supprimé"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const quickStock = async (p, stock) => {
    try { await api.put(`/admin/products/${p.id}`, { ...p, stock }); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const list = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const catName = (list) => (list || []).map((s) => cats.find((c) => c.slug === s)?.name || s).join(", ");

  return (
    <div data-testid="admin-products">
      <AdminTitle title="Produits & stocks" action={<button className="btn-gold !py-3" onClick={() => setEditing({ ...EMPTY })} data-testid="admin-add-product-button"><Plus className="h-4 w-4" />Ajouter un produit</button>}>
        {products.length} produit(s). Modifiez prix, stocks, photos et descriptions.
      </AdminTitle>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="mb-4 w-full sm:w-72 bg-transparent border border-[#C9A45C]/25 px-3 py-2 text-sm outline-none" data-testid="admin-products-search" />
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 border-b border-[#C9A45C]/15">
            <th className="p-3">Produit</th><th className="p-3">Catégorie</th><th className="p-3">Prix</th><th className="p-3">Stock</th><th className="p-3">Vendus</th><th className="p-3">Statut</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-[#C9A45C]/10 hover:bg-[#C9A45C]/5" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-3"><div className="flex items-center gap-3"><img src={imgUrl(p.images?.[0])} alt="" className="h-12 w-10 object-cover" /><div><p>{p.name}</p><p className="text-xs text-[#F8F6F1]/45">{p.volume_ml ? `${p.volume_ml} ml · ` : ""}{p.weight_g} g{p.weight_verified ? "" : " (à vérifier)"}</p></div></div></td>
                <td className="p-3 text-[#F8F6F1]/70 text-xs">{catName(p.categories)}</td>
                <td className="p-3 text-[#C9A45C]">{formatPrice(p.price)}</td>
                <td className="p-3">
                  <input type="number" min="0" defaultValue={p.stock} onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v >= 0 && v !== p.stock) quickStock(p, v); }}
                    className={`w-20 bg-transparent border px-2 py-1 ${p.stock === 0 ? "border-red-400/60 text-red-400" : p.stock <= 3 ? "border-amber-300/60 text-amber-300" : "border-[#C9A45C]/25"}`} data-testid={`admin-stock-input-${p.slug}`} />
                </td>
                <td className="p-3">{p.sold_count || 0}</td>
                <td className="p-3 text-xs">{p.active ? "Visible" : "Masqué"}{p.is_example && <span className="ml-2 text-[#C9A45C]">Exemple</span>}</td>
                <td className="p-3"><div className="flex gap-1 justify-end">
                  <button onClick={() => edit(p)} className="p-2 hover:text-[#C9A45C]" aria-label="Modifier" data-testid={`admin-edit-product-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} className="p-2 hover:text-red-400" aria-label="Supprimer" data-testid={`admin-delete-product-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#101010] border-[#C9A45C]/30 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl font-normal">{editing?.id ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          {editing && <ProductForm initial={editing} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
